// Section 22 Build tab (stream M3a): buildings owned with condition, effect and upkeep; the build
// queue as it will stand after this season's draft commands (the same order the decide phase
// applies them), with cost, materials on hand versus needed, and seasons to completion at the
// current builder count; what can be queued (limits as validateDecision checks them); housing.
import {
  buildLimit,
  buildPointsForecast,
  catalog,
  evaluateTarget,
  housingSummary,
  orderCost,
  upkeepOf,
  type BuildingId,
  type BuildingTypeId,
  type BuildOrderId,
  type Decision,
  type Factor,
  type GameState,
  type HousingPolicy,
  type Resource,
} from '../sim/api';

export type Materials = Partial<Record<Resource, number>>;

export interface BuildOption {
  id: BuildingTypeId;
  label: string;
  /** Base build points (Section 9 table). */
  buildPoints: number;
  /** Build points through the pipeline, with its explanation. */
  cost: number;
  costFactors: Factor[];
  materials: Materials;
  /** Materials missing from stock right now, per resource. */
  shortfall: Materials;
  owned: number;
  /** Owned plus queued (including this season's draft). */
  planned: number;
  /** null when the building has no limit. */
  limit: number | null;
  atLimit: boolean;
  beds: number;
  plots: number;
  capacity: Materials;
}

export type QueueStatus = 'building' | 'next' | 'waiting' | 'blocked' | 'paused' | 'at-limit';

export interface QueueRow {
  /** null for a building queued in this season's draft (not yet an order). */
  id: BuildOrderId | null;
  /** Index among the draft's queue commands (for removing a pending item). */
  pendingIndex: number | null;
  /** Position in the queue after this season's commands. */
  index: number;
  type: BuildingTypeId;
  label: string;
  repairOf: BuildingId | null;
  progress: number;
  required: number;
  costFactors: Factor[];
  materials: Materials;
  materialsPaid: boolean;
  /** Missing materials for an order that has not started, against the stock left after the
   *  orders ahead of it start. */
  shortfall: Materials;
  pending: boolean;
  paused: boolean;
  /**
   * Section 9: what cancelling returns now: 50% of the building's full materials once paid (the
   * decide phase refunds the full list, also for repairs), nothing before construction starts.
   */
  refund: Materials;
  status: QueueStatus;
  /** Seasons until finished at the current builder count, or null if it will not progress. */
  seasons: number | null;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

export interface CancelledRow {
  id: BuildOrderId;
  label: string;
  /** Section 9: materials already paid come back at 50%. */
  refund: Materials;
}

export interface BuildingRow {
  id: BuildingId;
  type: BuildingTypeId;
  label: string;
  condition: number;
  /** Effect strength 0..1 (the `buildingEffect` target) and its explanation. */
  effect: number;
  effectFactors: Factor[];
  frozen: boolean;
  /** Seasons until upkeep is due; 0 or less means due or overdue. */
  upkeepIn: number;
  upkeep: Materials;
  upkeepAffordable: boolean;
  repairQueued: boolean;
  canRepair: boolean;
}

export interface HousingRow {
  policy: HousingPolicy;
  beds: number;
  people: number;
  rough: number;
  crowdedHouseholds: number;
}

export interface BuildVM {
  options: BuildOption[];
  queue: QueueRow[];
  cancelled: CancelledRow[];
  buildings: BuildingRow[];
  builders: number;
  pointsPerSeason: number;
  stock: Materials;
  housing: HousingRow;
  /** Section 9: share of paid materials a cancelled order returns. */
  cancelRefund: number;
}

interface Planned {
  id: BuildOrderId | null;
  pendingIndex: number | null;
  type: BuildingTypeId;
  repairOf: BuildingId | null;
  pointsPaid: number;
  pointsRequired: number;
  materialsPaid: boolean;
  paused: boolean;
}

const MATERIALS: readonly Resource[] = ['wood', 'stone'];

function clampIndex(i: number, length: number): number {
  return Math.max(0, Math.min(length, i));
}

/**
 * The queue after this season's draft commands, applied in order exactly as the decide phase
 * applies them (Section 4 phase 2): queue appends, cancel removes, pause toggles, move reorders.
 */
export function plannedQueue(
  game: GameState,
  draft: Decision | null,
): { queue: Planned[]; cancelled: Planned[] } {
  const queue: Planned[] = game.buildQueue.map((o) => ({
    id: o.id,
    pendingIndex: null,
    type: o.buildingType,
    repairOf: o.repairOf,
    pointsPaid: o.pointsPaid,
    pointsRequired: o.pointsRequired,
    materialsPaid: o.materialsPaid,
    paused: o.paused,
  }));
  const cancelled: Planned[] = [];
  let pending = 0;
  for (const cmd of draft?.builds ?? []) {
    if (cmd.kind === 'queue') {
      queue.push({
        id: null,
        pendingIndex: pending++,
        type: cmd.buildingType,
        repairOf: cmd.repairOf,
        pointsPaid: 0,
        pointsRequired: orderCost(game, cmd.buildingType, null).points,
        materialsPaid: false,
        paused: false,
      });
      continue;
    }
    const at = queue.findIndex((o) => o.id === cmd.orderId);
    const order = queue[at];
    if (order === undefined) continue;
    if (cmd.kind === 'cancel') {
      queue.splice(at, 1);
      cancelled.push(order);
    } else if (cmd.kind === 'pause') {
      order.paused = cmd.paused;
    } else {
      queue.splice(at, 1);
      queue.splice(clampIndex(cmd.toIndex, queue.length), 0, order);
    }
  }
  return { queue, cancelled };
}

function subtract(stock: Materials, cost: Materials): Materials {
  const out: Materials = { ...stock };
  for (const r of MATERIALS) out[r] = (out[r] ?? 0) - (cost[r] ?? 0);
  return out;
}

function shortOf(stock: Materials, cost: Materials): Materials {
  const out: Materials = {};
  for (const r of MATERIALS) {
    const short = (cost[r] ?? 0) - (stock[r] ?? 0);
    if (short > 1e-9) out[r] = short;
  }
  return out;
}

const isEmpty = (m: Materials): boolean => Object.keys(m).length === 0;

function refundOf(game: GameState, o: Planned, share: number): Materials {
  if (!o.materialsPaid) return {};
  const out: Materials = {};
  for (const [r, n] of Object.entries(orderCost(game, o.type, null).materials) as [
    Resource,
    number,
  ][])
    out[r] = n * share;
  return out;
}

function queueRows(
  game: GameState,
  planned: Planned[],
  label: (t: BuildingTypeId) => string,
  pointsPerSeason: number,
  share: number,
): QueueRow[] {
  let stock: Materials = { wood: game.stocks.wood.amount, stone: game.stocks.stone.amount };
  let cumulative = 0;
  const counts = new Map<BuildingTypeId, number>();
  let first = true;
  return planned.map((o, index) => {
    const cost = orderCost(game, o.type, o.repairOf);
    // An order's points are fixed when it starts (a repair is scaled by the damage then).
    const required = o.materialsPaid ? o.pointsRequired : cost.points;
    const needPoints = Math.max(0, required - o.pointsPaid);
    const shortfall = o.materialsPaid ? {} : shortOf(stock, cost.materials);
    const owned = Object.values(game.buildings).filter((b) => b.type === o.type).length;
    const counted = (counts.get(o.type) ?? 0) + (o.repairOf === null ? 1 : 0);
    counts.set(o.type, counted);
    let status: QueueStatus;
    let seasons: number | null = null;
    if (o.paused) status = 'paused';
    else if (o.repairOf === null && owned + counted > buildLimit(game, o.type)) status = 'at-limit';
    else if (!isEmpty(shortfall)) status = 'blocked';
    else {
      if (!o.materialsPaid) stock = subtract(stock, cost.materials);
      cumulative += needPoints;
      seasons =
        pointsPerSeason > 1e-9 ? Math.max(1, Math.ceil(cumulative / pointsPerSeason - 1e-9)) : null;
      status = o.materialsPaid ? 'building' : first ? 'next' : 'waiting';
      first = false;
    }
    return {
      id: o.id,
      pendingIndex: o.pendingIndex,
      index,
      type: o.type,
      label: label(o.type),
      repairOf: o.repairOf,
      progress: o.pointsPaid,
      required,
      costFactors: evaluateTarget(game, { kind: 'buildCost', buildingType: o.type }).factors,
      materials: cost.materials,
      materialsPaid: o.materialsPaid,
      shortfall,
      pending: o.id === null,
      paused: o.paused,
      refund: refundOf(game, o, share),
      status,
      seasons,
      canMoveUp: o.id !== null && index > 0,
      canMoveDown: o.id !== null && index < planned.length - 1,
    };
  });
}

export function buildVM(game: GameState, draft: Decision | null): BuildVM {
  const cat = catalog();
  const label = (id: BuildingTypeId): string => cat.buildings.find((b) => b.id === id)?.label ?? id;
  const stock: Materials = { wood: game.stocks.wood.amount, stone: game.stocks.stone.amount };
  const { queue, cancelled } = plannedQueue(game, draft);
  const forecast = buildPointsForecast(game, draft);
  const owned = (id: BuildingTypeId): number =>
    Object.values(game.buildings).filter((b) => b.type === id).length;
  const planned = (id: BuildingTypeId): number =>
    owned(id) + queue.filter((o) => o.type === id && o.repairOf === null).length;
  const repairing = new Set(queue.flatMap((o) => (o.repairOf === null ? [] : [o.repairOf])));
  const draftPolicy = draft?.policies.housing;
  const housing = housingSummary(game);
  return {
    options: cat.buildings.map((b) => {
      const limit = buildLimit(game, b.id);
      const cost = evaluateTarget(game, { kind: 'buildCost', buildingType: b.id });
      return {
        id: b.id,
        label: b.label,
        buildPoints: b.buildPoints,
        cost: cost.value,
        costFactors: cost.factors,
        materials: b.materials,
        shortfall: shortOf(stock, b.materials),
        owned: owned(b.id),
        planned: planned(b.id),
        limit: b.limit.kind === 'none' ? null : limit,
        atLimit: planned(b.id) >= limit,
        beds: b.beds,
        plots: b.plots,
        capacity: b.capacity,
      };
    }),
    queue: queueRows(game, queue, label, forecast.points, cat.building.cancelRefund),
    cancelled: cancelled.flatMap((o) =>
      o.id === null
        ? []
        : [
            {
              id: o.id,
              label: label(o.type),
              refund: refundOf(game, o, cat.building.cancelRefund),
            },
          ],
    ),
    buildings: Object.values(game.buildings)
      .sort((a, b) => (a.id < b.id ? -1 : 1))
      .map((b) => {
        const effect = evaluateTarget(game, { kind: 'buildingEffect', buildingId: b.id });
        const upkeep = upkeepOf(game, b.id);
        return {
          id: b.id,
          type: b.type,
          label: label(b.type),
          condition: b.condition,
          effect: effect.value,
          effectFactors: effect.factors,
          frozen: b.frozen,
          upkeepIn: b.upkeepDueTurn - game.turn,
          upkeep,
          upkeepAffordable: isEmpty(shortOf(stock, upkeep)),
          repairQueued: repairing.has(b.id),
          canRepair: b.condition < 100 && !repairing.has(b.id),
        };
      }),
    builders: forecast.builders,
    pointsPerSeason: forecast.points,
    stock,
    cancelRefund: cat.building.cancelRefund,
    housing: {
      policy: draftPolicy ?? game.policies.housing,
      beds: housing.beds,
      people: housing.people,
      rough: housing.rough,
      crowdedHouseholds: housing.crowdedHouseholds.length,
    },
  };
}
